import React, { useEffect, useMemo, useState } from "react";
import BillingOverview from "../components/Billings/BillingOverview";
import PlanCard from "../components/Billings/PlanCard";
import FreeTrialModal from "../components/Modals/FreeTrialModal";
import { useDispatch, useSelector } from "react-redux";
import { ownerSelector } from "../store/store";
import { Button } from "../components/ui";
import { createCheckoutSession, getActivatedTrialMessage } from "../utils/checkout";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaCheckCircle } from "react-icons/fa";
import { refreshBillingSession } from "../components/Billings/billingSession";
import { PAYMENT_URL, BOOK_DEMO_CALL_URL } from "../constants";
import { resolveBillingSource } from "../utils/billingSource";
import {
  FALLBACK_PRICING_CATALOG,
  fetchPricingCatalog,
  getGrowthPlanName,
  getProfessionalPlanName,
  getVisibleIntervals,
  getVisiblePlans,
  isLaunchFreeBillingCatalog,
  shouldShowEnterprisePlan,
  type BillingInterval,
  type PricingCatalog,
} from "../utils/pricingCatalog";

type Props = {};

const Billings = (_props: Props) => {
  const { owner, currentUser } = useSelector(ownerSelector);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [interval, setInterval] = useState<BillingInterval>("monthly");
  const [pricingCatalog, setPricingCatalog] = useState<PricingCatalog>(
    FALLBACK_PRICING_CATALOG
  );
  const isLaunchFreeBilling = isLaunchFreeBillingCatalog(pricingCatalog);
  const isSafepayBilling = String(pricingCatalog.billingMode || "").toLowerCase() === "safepay";
  const visiblePlans = getVisiblePlans(pricingCatalog);
  const visibleIntervals = getVisibleIntervals(pricingCatalog);
  const showEnterprise = shouldShowEnterprisePlan(pricingCatalog);
  const [isCancellingScheduledChange, setIsCancellingScheduledChange] = useState(false);
  const [isCompletingScheduledCheckout, setIsCompletingScheduledCheckout] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    if (!status) return;

    if (status === "success") {
      toast.success("Checkout completed successfully.");
      refreshBillingSession({ owner, currentUser, dispatch }).catch(() => {});
    } else if (status === "cancel") {
      toast.info("Checkout was canceled.");
    }

    navigate({ pathname: location.pathname, search: "" }, { replace: true });
  }, [location.search, owner?._id, owner, currentUser?.userId, currentUser?.email, dispatch, navigate]);

  // Always re-fetch billing when opening this page (e.g. after Safepay card-save return).
  useEffect(() => {
    refreshBillingSession({ owner, currentUser, dispatch }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh once on mount / route entry
  }, [location.pathname]);

  // If user returns from Safepay tab without hitting /payment/success, pick up activation.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      refreshBillingSession({ owner, currentUser, dispatch }).catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.userId, owner?._id]);

  useEffect(() => {
    fetchPricingCatalog(PAYMENT_URL)
      .then((data) => {
        setPricingCatalog(data);
        const intervals = getVisibleIntervals(data);
        if (!intervals.includes(interval)) {
          setInterval(intervals[0] || "monthly");
        }
      })
      .catch(() => {});
  }, []);

  const prefillEmail = useMemo(() => {
    const e =
      (typeof owner?.email === "string" && owner.email.trim()) ||
      (typeof currentUser?.email === "string" && currentUser.email.trim()) ||
      "";
    return e || undefined;
  }, [owner?.email, currentUser?.email]);
  const billingSource = resolveBillingSource({ owner, currentUser });
  const pendingPlanChange =
    (billingSource as any)?.pendingPlanChange ||
    (owner as any)?.userBilling?.pendingPlanChange ||
    (owner as any)?.pendingPlanChange ||
    null;
  const resolvedUserId =
    (typeof currentUser?.userId === "string" && currentUser.userId) ||
    (typeof (owner as any)?.userId === "string" && (owner as any).userId) ||
    "";

  const pendingTargetPlanLabel = (() => {
    const key = String(pendingPlanChange?.targetPlanKey || "").toUpperCase();
    if (key === "GROWTH") return getGrowthPlanName(pricingCatalog);
    if (key === "PROFESSIONAL") return getProfessionalPlanName(pricingCatalog);
    return String(pendingPlanChange?.targetPlanKey || "");
  })();

  const completeAwaitingCheckout = async () => {
    if (!resolvedUserId || !pendingPlanChange?.targetPlanKey) {
      toast.error("User session not loaded. Please refresh and try again.");
      return;
    }
    try {
      setIsCompletingScheduledCheckout(true);
      const checkout = await createCheckoutSession({
        ownerId: owner?._id,
        userId: resolvedUserId,
        plan: String(pendingPlanChange.targetPlanKey).toLowerCase() as
          | "growth"
          | "professional",
        interval: (String(pendingPlanChange.targetInterval || "monthly").toLowerCase() ||
          "monthly") as BillingInterval,
        trial: false,
      });
      if (checkout.mode === "checkout") {
        window.location.href = checkout.url;
        return;
      }
      toast.error("Unable to open checkout for the scheduled plan.");
    } catch (e: any) {
      toast.error(e?.message || "Unable to open checkout.");
    } finally {
      setIsCompletingScheduledCheckout(false);
    }
  };

  const billingHistory = Array.isArray((billingSource as any)?.planhistory)
    ? (billingSource as any).planhistory
    : [];
  const hasNoPlan = billingHistory.length === 0;
  const hasFreePlanOnly = (() => {
    if (!Array.isArray(billingHistory) || billingHistory.length === 0) return false;
    const last = billingHistory[billingHistory.length - 1];
    return last?.planName === "Free Plan";
  })();
  const hasUsedTrial = billingHistory.some((p: any) => p?.isTrial === true);
  const shouldShowEarlyJoinerBanner = isLaunchFreeBilling
    ? (hasNoPlan || hasFreePlanOnly) && !hasUsedTrial
    : false;

  const plans = useMemo(() => {
    const items: Array<{
      currencySymbol: string;
      amount: number;
      billedLine?: string;
      oldPriceDisplay?: number;
      name: string;
      planId: string;
      features: string[];
      discount: number;
      showDiscount: boolean;
      paymentUrl: string;
      checkoutPlan: "growth" | "professional";
      checkoutInterval: BillingInterval;
    }> = [];

    if (visiblePlans.includes("growth")) {
      items.push({
        currencySymbol: "$",
        amount: pricingCatalog.computed.growth[interval].displayMonthly,
        billedLine: pricingCatalog.computed.growth[interval].billedLine,
        oldPriceDisplay:
          interval === "monthly"
            ? undefined
            : pricingCatalog.computed.growth[interval].oldPriceMonthly,
        name: getGrowthPlanName(pricingCatalog),
        planId: "Innlead Plan",
        features: pricingCatalog.plans.growth.features,
        discount:
          interval === "monthly"
            ? 0
            : interval === "quarterly"
            ? pricingCatalog.discounts.quarterlyPercent
            : pricingCatalog.discounts.yearlyPercent,
        showDiscount: interval !== "monthly",
        paymentUrl: "",
        checkoutPlan: "growth",
        checkoutInterval: interval,
      });
    }

    if (visiblePlans.includes("professional")) {
      items.push({
        currencySymbol: "$",
        amount: pricingCatalog.computed.professional[interval].displayMonthly,
        billedLine: pricingCatalog.computed.professional[interval].billedLine,
        oldPriceDisplay:
          interval === "monthly"
            ? undefined
            : pricingCatalog.computed.professional[interval].oldPriceMonthly,
        name: getProfessionalPlanName(pricingCatalog),
        planId: "Professional Plan",
        features: pricingCatalog.plans.professional.features,
        discount:
          interval === "monthly"
            ? 0
            : interval === "quarterly"
            ? pricingCatalog.discounts.quarterlyPercent
            : pricingCatalog.discounts.yearlyPercent,
        showDiscount: interval !== "monthly",
        paymentUrl: "",
        checkoutPlan: "professional",
        checkoutInterval: interval,
      });
    }

    return items;
  }, [interval, pricingCatalog, visiblePlans]);

  const isSingleFeaturedPlan = plans.length === 1 && !showEnterprise;

  const gridClassName = isSingleFeaturedPlan
    ? "w-full"
    : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 items-stretch";

  return (
    <section className="h-full min-h-0 overflow-y-auto overflow-x-hidden p-4 sm:p-6">
      <div className="mx-auto flex w-full max-w-[1240px] flex-col gap-5">
        <BillingOverview
          isLaunchFreeBilling={isLaunchFreeBilling}
          launchTrialLabel={pricingCatalog.launchTrialLabel}
          growthPlanName={getGrowthPlanName(pricingCatalog)}
        />
        {shouldShowEarlyJoinerBanner ? (
          <div className="rounded-md border border-mainColor/20 bg-mainColor/5 px-4 py-3 text-sm text-dark-black">
            {pricingCatalog.launchTrialLabel
              ? `${pricingCatalog.launchTrialLabel.charAt(0).toUpperCase()}${pricingCatalog.launchTrialLabel.slice(1)}. ${
                  isLaunchFreeBilling
                    ? "No payment required — create an account to get started."
                    : isSafepayBilling
                      ? "Enter your card securely to continue — no Safepay account or login required. Cancel anytime."
                      : "A credit card is required — cancel anytime."
                }`
              : isLaunchFreeBilling
              ? "Early joiners get a 1.5-month free trial. No payment required — create an account to get started."
              : isSafepayBilling
                ? "Start with a 7-day free trial. Enter your card securely to continue — no Safepay account or login required. Cancel anytime."
                : "Start with a 7-day free trial. A credit card is required — cancel anytime."}
          </div>
        ) : null}
        {pendingPlanChange?.status === "pending" ? (
          <div className="rounded-md border border-mainColor/20 bg-mainColor/5 px-3 py-2 text-sm text-dark-black">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p>
                Plan change scheduled to{" "}
                <strong>
                  {pendingTargetPlanLabel}{" "}
                  ({pendingPlanChange?.targetInterval}
                  {pendingPlanChange?.targetSeatCount
                    ? `, ${pendingPlanChange.targetSeatCount} seat${
                        pendingPlanChange.targetSeatCount === 1 ? "" : "s"
                      }`
                    : ""}
                  )
                </strong>{" "}
                on{" "}
                <strong>
                  {pendingPlanChange?.effectiveAt
                    ? new Date(pendingPlanChange.effectiveAt).toLocaleDateString()
                    : "period end"}
                </strong>
                {isSafepayBilling
                  ? ". After that date, complete checkout on Billing to activate the new plan."
                  : "."}
              </p>
              <Button
                variant="outline"
                size="sm"
                isDisabled={isCancellingScheduledChange || !resolvedUserId}
                onClick={async () => {
                  if (!resolvedUserId) {
                    toast.error("User session not loaded. Please refresh and try again.");
                    return;
                  }
                  try {
                    setIsCancellingScheduledChange(true);
                    const resp = await fetch(`${PAYMENT_URL}/cancelScheduledPlanChange`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ userId: resolvedUserId }),
                    });
                    const data = await resp.json();
                    if (!resp.ok) {
                      toast.error(data?.error || "Unable to cancel scheduled plan change.");
                      return;
                    }
                    toast.success("Scheduled plan change cancelled.");
                    await refreshBillingSession({ owner, currentUser, dispatch });
                  } catch (e: any) {
                    toast.error(
                      e?.message || "Unable to cancel scheduled plan change."
                    );
                  } finally {
                    setIsCancellingScheduledChange(false);
                  }
                }}
              >
                {isCancellingScheduledChange
                  ? "Cancelling..."
                  : "Cancel Scheduled Change"}
              </Button>
            </div>
          </div>
        ) : null}
        {pendingPlanChange?.status === "awaiting_checkout" ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-dark-black">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p>
                Your previous plan has ended. Complete checkout to activate{" "}
                <strong>
                  {pendingTargetPlanLabel} ({pendingPlanChange?.targetInterval})
                </strong>
                .
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  isDisabled={isCompletingScheduledCheckout || !resolvedUserId}
                  onClick={completeAwaitingCheckout}
                >
                  {isCompletingScheduledCheckout ? "Opening..." : "Complete checkout"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  isDisabled={isCancellingScheduledChange || !resolvedUserId}
                  onClick={async () => {
                    if (!resolvedUserId) return;
                    try {
                      setIsCancellingScheduledChange(true);
                      const resp = await fetch(`${PAYMENT_URL}/cancelScheduledPlanChange`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ userId: resolvedUserId }),
                      });
                      const data = await resp.json();
                      if (!resp.ok) {
                        toast.error(data?.error || "Unable to dismiss scheduled change.");
                        return;
                      }
                      toast.success("Scheduled plan change cleared.");
                      await refreshBillingSession({ owner, currentUser, dispatch });
                    } catch (e: any) {
                      toast.error(e?.message || "Unable to dismiss scheduled change.");
                    } finally {
                      setIsCancellingScheduledChange(false);
                    }
                  }}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="font-semibold text-md text-dark-black">
            {plans.length === 1 ? "Plan" : "Plans"}
          </h3>
          {visibleIntervals.length > 1 ? (
            <div className="inline-flex w-full items-center gap-0 rounded-full border border-light-grey bg-white p-1 shadow-sm sm:w-auto">
              {[
                { key: "monthly", label: "Monthly" },
                { key: "quarterly", label: "Quarterly -15%" },
                { key: "yearly", label: "Yearly -25%" },
              ]
                .filter((m) => visibleIntervals.includes(m.key as BillingInterval))
                .map((m) => {
                  const active = m.key === interval;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => setInterval(m.key as BillingInterval)}
                      className={[
                        "flex-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors sm:flex-none",
                        active
                          ? "bg-mainColor text-white shadow-sm"
                          : "text-dark-grey hover:bg-primary",
                      ].join(" ")}
                    >
                      {m.label}
                    </button>
                  );
                })}
            </div>
          ) : null}
        </div>

        <div className={gridClassName}>
          {plans.map((plan, index) => (
            <PlanCard
              key={index}
              {...plan}
              isLaunchFreeBilling={isLaunchFreeBilling}
              isSafepayBilling={isSafepayBilling}
              launchTrialLabel={pricingCatalog.launchTrialLabel}
              onOpenTrialModal={() => setShowTrialModal(true)}
              featuredLayout={isSingleFeaturedPlan}
              description={
                plan.checkoutPlan === "professional"
                  ? pricingCatalog.plans.professional.description
                  : pricingCatalog.plans.growth.description
              }
              footerLine={
                isLaunchFreeBilling
                  ? (plan.checkoutPlan === "professional"
                      ? pricingCatalog.plans.professional.footerLine
                      : pricingCatalog.plans.growth.footerLine) ||
                    "No credit card required — early joiner offer"
                  : plan.checkoutPlan === "professional"
                  ? pricingCatalog.plans.professional.footerLine
                  : pricingCatalog.plans.growth.footerLine
              }
            />
          ))}
          {showEnterprise ? (
            <div className="relative flex h-full min-h-[480px] flex-col rounded-[8px] border border-light-grey bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.06)]">
              <div className="flex flex-1 flex-col">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-dark-black">
                    {pricingCatalog.plans.enterprise.name || "Enterprise"}
                  </h3>
                </div>
                <div className="mt-3 flex flex-1 flex-col gap-2">
                  <div className="flex items-center">
                    <p className="text-mainColor font-semibold text-2xl">Contact us</p>
                  </div>
                  <div className="flex flex-1 flex-col gap-2 overflow-auto pr-1">
                    {pricingCatalog.plans.enterprise.features.map((feature, index) => (
                      <p className="text-sm text-dark-grey flex items-center gap-2" key={index}>
                        <FaCheckCircle size={12} className="text-mainColor" />
                        {feature}
                      </p>
                    ))}
                  </div>
                </div>
                <div className="mt-4">
                  <Button
                    variant="primary"
                    size="sm"
                    fullWidth
                    onClick={() => {
                      if (!BOOK_DEMO_CALL_URL) return;
                      window.open(BOOK_DEMO_CALL_URL, "_blank", "noopener,noreferrer");
                    }}
                  >
                    Contact Sales
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <FreeTrialModal
        isOpen={showTrialModal}
        onClose={() => setShowTrialModal(false)}
        prefillEmail={prefillEmail}
        isLaunchFreeBilling={isLaunchFreeBilling}
        onScheduleCall={() => {
          if (!BOOK_DEMO_CALL_URL) return;
          window.open(BOOK_DEMO_CALL_URL, "_blank");
          setShowTrialModal(false);
        }}
        onActivateTrial={({ interval: trialInterval }) => {
          if (!resolvedUserId) return;
          createCheckoutSession({
            ownerId: owner?._id,
            userId: resolvedUserId,
            plan: "growth",
            interval: trialInterval,
            trial: true,
          })
            .then((checkout) => {
              if (checkout.mode === "scheduled") {
                toast.success(
                  checkout.message || "Plan change scheduled for current period end."
                );
                refreshBillingSession({ owner, currentUser, dispatch }).catch(() => {});
                return;
              }
              if (checkout.mode === "activated") {
                toast.success(
                  getActivatedTrialMessage(
                    checkout,
                    pricingCatalog.launchTrialLabel
                  )
                );
                refreshBillingSession({ owner, currentUser, dispatch }).catch(() => {});
                setShowTrialModal(false);
                return;
              }
              window.location.href = checkout.url;
            })
            .catch(() => {});
        }}
      />
    </section>
  );
};

export default Billings;
